import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Button,
  TextInput,
  Group,
  Badge,
  ActionIcon,
  Modal,
  Stack,
  Alert,
  Loader,
  Pagination,
  Select,
  Box,
  Grid,
  Divider,
  Tabs
} from '@mantine/core';
import { 
  IconSearch, 
  IconEdit, 
  IconTrash, 
  IconEye, 
  IconPlus,
  IconUserPlus,
  IconUserMinus,
  IconCreditCard,
  IconReceipt,
  IconMail,
  IconReport
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { API_URL } from '../../../utils/constants';

function GuestsListScreen() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    fetchGuests();
  }, [currentPage, searchTerm]);

  const fetchGuests = async () => {
    try {
    setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/guests?page=${currentPage}&search=${searchTerm}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('API Response received:', data);
        console.log('Guests data:', data.guests);
        if (data.guests && data.guests.length > 0) {
          console.log('First guest from API:', data.guests[0]);
          console.log('First guest firstname:', data.guests[0].firstname);
          console.log('First guest lastname:', data.guests[0].lastname);
        }
        setGuests(data.guests || []);
        setTotalPages(data.pagination?.totalPages || 1);
      } else {
        setError('Failed to fetch guests');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (guestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/guests/${guestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });
      if (response.ok) {
        notifications.show({
          title: 'Success',
          message: 'Guest deleted successfully',
          color: 'green'
        });
        fetchGuests();
        setShowDeleteModal(false);
    } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to delete guest',
          color: 'red'
        });
      }
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'Network error',
        color: 'red'
      });
    }
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/guests/${selectedGuest.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(selectedGuest),
      });

      if (response.ok) {
        const updatedGuest = await response.json();
        notifications.show({
          title: 'Success',
          message: 'Guest updated successfully',
          color: 'green'
        });
        setShowEditModal(false);
        setSelectedGuest(null);
        fetchGuests(); // Refresh the list
      } else {
        const errorData = await response.json();
        notifications.show({
          title: 'Error',
          message: errorData.message || 'Error updating guest',
          color: 'red'
        });
      }
    } catch (error) {
      console.error('Error updating guest:', error);
      notifications.show({
        title: 'Error',
        message: 'Error updating guest. Please try again.',
        color: 'red'
      });
    }
  };

  const handleGuestSelect = (guest) => {
    setSelectedGuest(guest);
    setActiveTab('details');
  };

  const rows = guests.map((guest) => {
    console.log('Rendering guest in Mantine:', guest);
    console.log('Guest firstname:', guest.firstname);
    console.log('Guest lastname:', guest.lastname);
    console.log('Guest phone1:', guest.phone1);
    return (
    <Table.Tr 
      key={guest.id}
      onClick={() => handleGuestSelect(guest)}
      style={{ 
        cursor: 'pointer',
        backgroundColor: selectedGuest && selectedGuest.id === guest.id ? '#ffff00' : '',
        border: selectedGuest && selectedGuest.id === guest.id ? '2px solid #ff0000' : ''
      }}
    >
      <Table.Td>{guest.guestnumber || guest.id}</Table.Td>
      <Table.Td>{`${guest.firstname} ${guest.lastname}`}</Table.Td>
      <Table.Td>{guest.email}</Table.Td>
      <Table.Td>
        <Badge color="blue" variant="light">
          {guest.guesttype || 'Standard'}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Badge color={guest.active ? 'green' : 'red'}>
          {guest.active ? 'Active' : 'Inactive'}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <ActionIcon
            variant="subtle"
            color="orange"
            onClick={(e) => { 
              e.stopPropagation();
              setSelectedGuest(guest);
              setShowEditModal(true);
            }}
          >
            <IconEdit size="1rem" />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={(e) => { 
              e.stopPropagation();
              setSelectedGuest(guest);
    setShowDeleteModal(true);
            }}
          >
            <IconTrash size="1rem" />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
    );
  });

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center">
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  return (
    <Container fluid p={0} style={{ height: '100vh' }}>
      <Grid gutter={0} style={{ height: '100%' }}>
        {/* Side Menu */}
        <Grid.Col span={1.2} style={{ backgroundColor: 'var(--mantine-color-gray-0)', padding: '0.5rem', borderRight: '1px solid var(--mantine-color-gray-2)' }}>
          <Stack gap="xs">
            <Title order={6} c="dark" mb="xs" ta="center">Guests</Title>
            
            {/* Primary Actions */}
              <Button 
              variant="subtle" 
              color="blue"
              fullWidth
              size="sm"
              onClick={() => window.location.href = '/dashboard/guests-find'}
              leftSection={<IconSearch size="0.8rem" />}
              >
                Find
              </Button>
              <Button 
              variant="subtle" 
              color="green"
              fullWidth
              size="sm"
              leftSection={<IconPlus size="0.8rem" />}
              onClick={() => window.location.href = '/dashboard/guests-add'}
              >
                Add
              </Button>
              <Button 
              variant="subtle" 
              color="orange"
              fullWidth
              size="sm"
              onClick={() => selectedGuest && setShowEditModal(true)}
                disabled={!selectedGuest}
              leftSection={<IconEdit size="0.8rem" />}
              >
                Change
              </Button>
              <Button 
              variant="subtle" 
              color="red"
              fullWidth
              size="sm"
              onClick={() => selectedGuest && setShowDeleteModal(true)}
                disabled={!selectedGuest}
              leftSection={<IconTrash size="0.8rem" />}
              >
                Delete
              </Button>
              
            <Divider color="gray.2" my="sm" />
              
            {/* Check In/Out Actions */}
              <Button 
              variant="subtle" 
              color="cyan"
              fullWidth
              size="sm"
              leftSection={<IconUserPlus size="0.8rem" />}
              >
                Check In
              </Button>
              <Button 
              variant="subtle" 
              color="teal"
              fullWidth
              size="sm"
              leftSection={<IconUserMinus size="0.8rem" />}
              >
                Check Out
              </Button>
              
            <Divider color="gray.2" my="sm" />
              
            {/* Financial Actions */}
              <Button 
              variant="subtle" 
              color="indigo"
              fullWidth
              size="sm"
              leftSection={<IconCreditCard size="0.8rem" />}
              >
                Post Charge
              </Button>
              <Button 
              variant="subtle" 
              color="violet"
              fullWidth
              size="sm"
              leftSection={<IconReceipt size="0.8rem" />}
              >
                Payment
              </Button>
              <Button 
              variant="subtle" 
              color="purple"
              fullWidth
              size="sm"
              leftSection={<IconReceipt size="0.8rem" />}
              >
                Refund
              </Button>
              
            <Divider color="gray.2" my="sm" />
              
            {/* Reservation Actions */}
              <Button 
              variant="subtle" 
              color="pink"
              fullWidth
              size="sm"
              >
                New Reservation
              </Button>
              
            <Divider color="gray.2" my="sm" />
              
            {/* Communication and Reports */}
              <Button 
              variant="subtle" 
              color="grape"
              fullWidth
              size="sm"
              leftSection={<IconMail size="0.8rem" />}
              >
                Emails
              </Button>
              <Button 
              variant="subtle" 
              color="dark"
              fullWidth
              size="sm"
              leftSection={<IconReport size="0.8rem" />}
              >
                Reports
              </Button>
          </Stack>
        </Grid.Col>

        {/* Main Content */}
        <Grid.Col span={10.8} style={{ padding: '1rem' }}>
          <Stack gap="lg">
            <Group justify="space-between">
              <Title order={1}>Guests List</Title>
            </Group>

          {error && (
              <Alert color="red" title="Error">
              {error}
            </Alert>
          )}

            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group mb="md">
                <TextInput
                  placeholder="Search guests..."
                  leftSection={<IconSearch size="1rem" />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ flex: 1 }}
                />
              </Group>

              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Guest Number</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.length > 0 ? rows : (
                    <Table.Tr>
                      <Table.Td colSpan={6} style={{ textAlign: 'center' }}>
                        <Text c="dimmed">No guests found</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>

              {totalPages > 1 && (
                <Group justify="center" mt="md">
                  <Pagination
                    value={currentPage}
                    onChange={setCurrentPage}
                    total={totalPages}
                  />
                </Group>
              )}
            </Paper>

            {/* Guest Details Section */}
            {selectedGuest && (
              <Paper shadow="sm" p="md" radius="md" withBorder>
                <Tabs value={activeTab} onChange={setActiveTab}>
                  <Tabs.List>
                    <Tabs.Tab value="details">Details</Tabs.Tab>
                    <Tabs.Tab value="reservations">Reservations</Tabs.Tab>
                    <Tabs.Tab value="notes">Notes</Tabs.Tab>
                    <Tabs.Tab value="history">History</Tabs.Tab>
                  </Tabs.List>

                  <Tabs.Panel value="details" pt="md">
                    <Grid>
                      <Grid.Col span={6}>
                        <Stack gap="xs">
                          <Text><strong>Guest Number:</strong> {selectedGuest.guestnumber || selectedGuest.id}</Text>
                          <Text><strong>Name:</strong> {selectedGuest.firstname} {selectedGuest.lastname}</Text>
                          <Text><strong>Type:</strong> {selectedGuest.guesttype || 'Standard'}</Text>
                          <Text><strong>Status:</strong> 
                            <Badge color={selectedGuest.active ? 'green' : 'red'} ml="xs">
                                    {selectedGuest.active ? 'Active' : 'Inactive'}
                                  </Badge>
                          </Text>
                          <Text><strong>Phone 1:</strong> {selectedGuest.phone1 || 'N/A'}</Text>
                          <Text><strong>Phone 2:</strong> {selectedGuest.phone2 || 'N/A'}</Text>
                          <Text><strong>Email:</strong> {selectedGuest.email || 'N/A'}</Text>
                          <Text><strong>Create Date:</strong> {selectedGuest.createdate ? new Date(selectedGuest.createdate).toLocaleDateString() : 'N/A'}</Text>
                        </Stack>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Stack gap="xs">
                          <Text><strong>Address 1:</strong> {selectedGuest.address1 || 'N/A'}</Text>
                          <Text><strong>Address 2:</strong> {selectedGuest.address2 || 'N/A'}</Text>
                          <Text><strong>City:</strong> {selectedGuest.city || 'N/A'}</Text>
                          <Text><strong>State:</strong> {selectedGuest.state || 'N/A'}</Text>
                          <Text><strong>Zipcode:</strong> {selectedGuest.zipcode || 'N/A'}</Text>
                          <Text><strong>Country:</strong> {selectedGuest.country || 'N/A'}</Text>
                        </Stack>
                      </Grid.Col>
                    </Grid>
                  </Tabs.Panel>

                  <Tabs.Panel value="reservations" pt="md">
                    <Text c="dimmed">Reservation history and upcoming stays will be displayed here.</Text>
                    <Text size="sm" c="dimmed" mt="xs">This feature will be implemented based on your specific requirements.</Text>
                  </Tabs.Panel>

                  <Tabs.Panel value="notes" pt="md">
                    <Text c="dimmed">Internal notes and communication history will be displayed here.</Text>
                    <Text size="sm" c="dimmed" mt="xs">This feature will be implemented based on your specific requirements.</Text>
                  </Tabs.Panel>

                  <Tabs.Panel value="history" pt="md">
                    <Text c="dimmed">Activity history and system interactions will be displayed here.</Text>
                    <Text size="sm" c="dimmed" mt="xs">This feature will be implemented based on your specific requirements.</Text>
                  </Tabs.Panel>
                </Tabs>
              </Paper>
            )}
          </Stack>
        </Grid.Col>
      </Grid>



      {/* Edit Guest Modal */}
      {console.log('showEditModal state:', showEditModal)}
      <Modal
        opened={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedGuest(null);
        }}
        title="Edit Guest"
        size="lg"
      >
        {selectedGuest && (
          <Stack gap="md">
            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Guest Number"
                  value={selectedGuest.guestnumber || ''}
                       disabled
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  label="Guest Type"
                  value={selectedGuest.guesttype || ''}
                  onChange={(value) => setSelectedGuest({...selectedGuest, guesttype: value})}
                  data={['Individual', 'Corporate', 'Group']}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="First Name *"
                  value={selectedGuest.firstname || ''}
                  onChange={(e) => setSelectedGuest({...selectedGuest, firstname: e.target.value})}
                       required
                     />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Last Name *"
                  value={selectedGuest.lastname || ''}
                  onChange={(e) => setSelectedGuest({...selectedGuest, lastname: e.target.value})}
                       required
                     />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Email"
                       type="email"
                  value={selectedGuest.email || ''}
                  onChange={(e) => setSelectedGuest({...selectedGuest, email: e.target.value})}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Phone 1"
                  value={selectedGuest.phone1 || ''}
                  onChange={(e) => setSelectedGuest({...selectedGuest, phone1: e.target.value})}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Phone 2"
                  value={selectedGuest.phone2 || ''}
                  onChange={(e) => setSelectedGuest({...selectedGuest, phone2: e.target.value})}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Group>
                  <Text size="sm">Status:</Text>
                  <Button
                    variant={selectedGuest.active ? 'filled' : 'outline'}
                    color={selectedGuest.active ? 'green' : 'gray'}
                    size="xs"
                    onClick={() => setSelectedGuest({...selectedGuest, active: !selectedGuest.active})}
                  >
                    {selectedGuest.active ? 'Active' : 'Inactive'}
                  </Button>
                </Group>
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Address 1"
                  value={selectedGuest.address1 || ''}
                  onChange={(e) => setSelectedGuest({...selectedGuest, address1: e.target.value})}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Address 2"
                  value={selectedGuest.address2 || ''}
                  onChange={(e) => setSelectedGuest({...selectedGuest, address2: e.target.value})}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={4}>
                <TextInput
                  label="City"
                  value={selectedGuest.city || ''}
                  onChange={(e) => setSelectedGuest({...selectedGuest, city: e.target.value})}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput
                  label="State"
                  value={selectedGuest.state || ''}
                  onChange={(e) => setSelectedGuest({...selectedGuest, state: e.target.value})}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput
                  label="Zipcode"
                  value={selectedGuest.zipcode || ''}
                  onChange={(e) => setSelectedGuest({...selectedGuest, zipcode: e.target.value})}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Country"
                  value={selectedGuest.country || ''}
                  onChange={(e) => setSelectedGuest({...selectedGuest, country: e.target.value})}
                />
              </Grid.Col>
            </Grid>

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={() => {
                setShowEditModal(false);
                setSelectedGuest(null);
              }}>
            Cancel
          </Button>
              <Button color="blue" onClick={handleSaveEdit}>
            Save Changes
          </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Guest"
        size="sm"
      >
        <Stack gap="md">
          <Text>Are you sure you want to delete this guest?</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
            <Button
              color="red"
              onClick={() => selectedGuest && handleDelete(selectedGuest.id)}
            >
            Delete
          </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default GuestsListScreen;
